import React, { useMemo } from 'react';
import type { FC } from 'react';

import { useHistory, Route, useLocation } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import Box from '@mui/material/Box';
import cloneDeep from 'lodash/cloneDeep';

import {
  Layout as NewLayout,
  RightSideBar,
  useRightSideBar
} from '@p81-common/ui';
import type { Theme } from '@p81-common/ui';

import MainHeader from 'components/Layout/parts/MainHeader/MainHeader';
import { HeaderMenus } from './parts/HeaderMenus/HeaderMenus';
import Routes from 'routes';
import Logo from 'components/Layout/navigation/Logo/Logo';
import LogoS from 'components/Layout/navigation/Logo/LogoS';
import Login from 'components/auth';

import {
  sidebarItemsMap as _sidebarItemsMap,
  SidebarItem
} from 'data/sidebarItemsMap';
import { useAuthState } from 'contexts/auth-context';
import { useRegion } from 'contexts/RegionContext';
import { useLayoutBehavior, useLayout, useHeaderHeight } from './Layout.hooks';
import { useRegionalLink } from 'hooks/useRegionalLink';
import {
  useBreadCrumbs,
  useNotifications,
  userControlsProps
} from './helpers/hooks';

import layoutContentBackground from 'assets/png/layout-content-background.png';

interface LayoutStyleProps {
  drawerLocked?: boolean;
  showDrawer?: boolean;
  fullHeight?: boolean;
}

// The top bar is `position: fixed`, so it is out of flow and the content
// container starts at y=0 behind it. Anything below therefore has to clear the
// bar's own height before the gap we actually want underneath it:
//
//   contentContainer paddingTop = header height + the desired gap
//
// The height belongs to @p81-common/ui and is not readable from a token. It
// measured 64.62px and 64.55px on two pages - the fraction means it derives
// from font metrics, so it moves with zoom and text scaling. Rather than pin
// it, `useHeaderHeight` observes the element and publishes the real value as a
// custom property; the constant below is only the pre-measurement fallback.
const HEADER_HEIGHT_FALLBACK = 65;
const HEADER_HEIGHT_VAR = '--jarvis-header-height';
const HEADER_HEIGHT = `var(${HEADER_HEIGHT_VAR}, ${HEADER_HEIGHT_FALLBACK}px)`;

const createClasses = makeStyles<Theme, LayoutStyleProps>(theme => ({
  layout: props => ({
    '& #layoutContainer': {
      // TODO: fix this TS error
      //@ts-ignore
      minWidth: `calc(${theme.breakpoints.values.customxs}px - ${theme.constants.scrollWidth})`,
      ...(props.fullHeight && { height: '100vh', overflow: 'hidden' }),

      '& > div:first-child > [data-testid="drawer"] > div': {
        position: 'fixed'
      },

      '& > div:last-child': {
        position: 'relative',
        flexDirection: 'row',
        alignContent: 'flex-start',

        // Left edge and width are deliberately not set: with `left: auto` the
        // fixed header takes its static position, i.e. exactly where it would
        // sit in flow after the sidebar, and follows the drawer at any width.
        // Pinning it to a hardcoded offset left a gap when that offset did not
        // match the real sidebar width.
        '& > header': {
          position: 'fixed',
          top: '0',
          zIndex: '98'
        }
      }
    }
  }),
  drawerRoot: {
    borderRight: `1px solid ${theme.palette.primary[200]}`,
    zIndex: 99
  },
  listItem: {
    color: theme.palette.primary[200],
    '&:hover': {
      '& path': {
        fill: theme.palette.primary[300]
      }
    },
    '& path': {
      fill: theme.palette.primary[300]
    },
    '&:focus': {
      '& path': {
        fill: theme.palette.primary[300]
      }
    }
  },
  subListItem: {
    color: theme.palette.common.white,
    '&:hover': {
      color: theme.palette.common.white
    }
  },

  listItemOpen: {
    '& path': {
      fill: theme.palette.primary[100]
    }
  },
  listItemSelected: {
    color: theme.palette.secondary[100],
    '& path': {
      fill: theme.palette.secondary[200]
    },
    '&:hover': {
      color: theme.palette.secondary[200],
      '& path': {
        fill: theme.palette.secondary[200]
      }
    },
    '&:focus': {
      color: theme.palette.secondary[200],
      '& path': {
        fill: theme.palette.secondary[200]
      }
    },
    '&:before': {
      borderRightColor: theme.palette.secondary[200]
    }
  },
  link: {
    '& #iconButton_1': {
      '& p, svg': {
        color: theme.palette.secondary[500],
        cursor: 'pointer'
      }
    }
  },
  contentContainer: props => {
    const { drawerLocked, showDrawer, fullHeight } = props;
    const drawerLockedStyle =
      drawerLocked && showDrawer
        ? {
            paddingRight: '422px'
          }
        : undefined;
    // Was `calc(100vh - 120px)`, consistent with the old model where the
    // in-flow subheader started this box at y=120. The bar is out of flow, so
    // the box now starts at y=0 and the bar is cleared by paddingTop instead:
    // the full viewport is the correct height, with border-box absorbing the
    // padding. Subtracting the bar as well would leave a gap at the bottom.
    const fullHeightStyle = fullHeight
      ? {
          height: '100vh',
          overflow: 'hidden' as const,
          '& > div': { height: '100%' }
        }
      : undefined;
    return {
      ...drawerLockedStyle,
      ...fullHeightStyle,
      backgroundImage: `url(${layoutContentBackground})`,
      backgroundPosition: 'top',
      backgroundRepeat: 'repeat-x',
      // Clears the fixed bar, then a 40px gap to the page's own header.
      paddingTop: `calc(${HEADER_HEIGHT} + ${theme.spacing(5)})`
    };
  }
}));

const LogoContainer = (props: { Component: FC }) => {
  const { Component } = props;
  const logoClasses = createClasses({});
  return (
    <div className={logoClasses.logo}>
      <Component />
    </div>
  );
};

const Layout = () => {
  // Handle user controls
  const history = useHistory();
  const location = useLocation();
  const generateRegionalPath = useRegionalLink();
  const { region } = useRegion();
  const { auth } = useAuthState();
  const { user } = auth;
  const { role } = user;

  const { drawerLocked, showDrawer } = useRightSideBar();

  const {
    handleCloseModal,
    anchorEl,
    modalOpen,
    userItems,
    handleSetAnchor,
    positionAnchor
  } = useLayout();

  const fullHeight = location.pathname === '/copilot-chat';
  const classes = createClasses({ drawerLocked, showDrawer, fullHeight });

  const { leftDrawerOpenState, setLeftDrawerOpenState, handleDrawerChange } =
    useLayoutBehavior();

  useHeaderHeight(HEADER_HEIGHT_VAR, location.pathname);

  const controlsProps = userControlsProps();

  const breadcrumbs = useBreadCrumbs();
  const { unreadCount, handleNotificationRing } = useNotifications();

  const sidebarItemsMap = useMemo(() => {
    // Create a deep copy to avoid mutating the original map
    const processedMap = cloneDeep(_sidebarItemsMap);

    if (role) {
      Object.keys(processedMap).forEach(option => {
        const currentOption = processedMap[option];
        if (!currentOption.permission) {
          delete processedMap[option];
        } else {
          currentOption.items = currentOption.items?.reduce(
            (acc: SidebarItem[], item) => {
              if (item.permission) {
                const newItem = { ...item };
                if (newItem.path) {
                  const result = generateRegionalPath(newItem.path);
                  newItem.path = result as string;
                }

                if (newItem.subItems) {
                  newItem.subItems = newItem.subItems
                    .filter(subItem => subItem.permission)
                    .map(subItem => {
                      const newSubItem = { ...subItem };
                      if (newSubItem?.path) {
                        const result = generateRegionalPath(newSubItem.path);
                        newSubItem.path = result as string;
                      }
                      return newSubItem;
                    });
                }

                return acc.concat(newItem);
              } else return acc;
            },
            []
          );
        }
      });
    }
    return processedMap;
  }, [role, generateRegionalPath]);

  return (
    <>
      <ScopedCssBaseline />
      {history.location.pathname === '/login' ? (
        <Route exact path="/login" component={Login} />
      ) : history.location.pathname === '/auth/callback' ? (
        <Routes />
      ) : !history.location.pathname.includes('/mobile') ? (
        <Box className={classes.layout}>
          <NewLayout
            classes={{
              contentContainer: classes.contentContainer
            }}
            headerProps={{
              ContentComponent: () => (
                <MainHeader
                  toggleNotificationsOpen={handleNotificationRing}
                  headerProps={{
                    breadcrumbsProps: {
                      customBreadcrumbs: breadcrumbs
                    }
                  }}
                  unreadCount={unreadCount}
                  userControlsProps={{
                    ...controlsProps,
                    userArrowCallback: handleSetAnchor()
                  }}
                />
              )
            }}
            sidebarProps={{
              classes,
              open: leftDrawerOpenState,
              setOpen: setLeftDrawerOpenState,
              handleDrawerChange: handleDrawerChange,
              sidebarItemsMap,
              logo: <LogoContainer Component={Logo} />,
              closedLogo: <LogoContainer Component={LogoS} />
            }}
          >
            <HeaderMenus
              anchorEl={anchorEl}
              positionAnchor={positionAnchor}
              handleSetAnchor={handleSetAnchor}
              userItems={userItems}
              modalOpen={modalOpen}
              handleCloseModal={handleCloseModal}
            />
            <Routes key={region.code} />
          </NewLayout>
          <RightSideBar />
        </Box>
      ) : (
        <Routes />
      )}
    </>
  );
};

export default Layout;
